import { useState, useEffect, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { renderInlineMarkdown } from '../../../utils/inlineMarkdown'
import { isEmbedAllowedUrl } from '../../../services/overviewService'
import { getCachedStorageFileUrl } from '../../../services/storageHubService'
import { ChartPlacementEmbed } from '../../Common/Chart/ChartPlacementEmbed'
import { Avatar } from '../../Common/Avatar'
import { Pressable } from '../../Common/Pressable'
import type { OverviewCustomSection, OverviewBlock } from '../../../types/overview'
import type { TeamPersonRow, ViewerTier } from '../../../utils/overviewInsights'
import type { WorkspaceItemReference } from '../../Common/WorkspaceItemPicker'
import type { ChartSpec } from '../../../types/chart'

export interface CustomSectionProps {
  section: OverviewCustomSection
  toolsNode?: ReactNode
  tier?: ViewerTier
  people?: TeamPersonRow[]
  workspaceId?: string
  isPhone?: boolean
  onNavigateToAnalysis?: (tab: 'CHARTS' | 'TEAM' | 'WORK') => void
  onOpenItem?: (ref: WorkspaceItemReference) => void
  onEditChart?: (spec: ChartSpec) => void
}

const ImageBlock = ({
  block,
}: {
  block: Extract<OverviewBlock, { type: 'IMAGE' }>
}) => {
  const [asyncSrc, setAsyncSrc] = useState<string>('')

  useEffect(() => {
    if ('uploadId' in block.source && block.source.uploadId) {
      let isMounted = true
      getCachedStorageFileUrl(block.source.uploadId)
        .then((url) => {
          if (isMounted) setAsyncSrc(url)
        })
        .catch(() => {
          // Leave empty on failure
        })
      return () => {
        isMounted = false
      }
    }
  }, [block.source])

  const src = 'url' in block.source ? block.source.url : asyncSrc

  const ratioStyle = block.ratio ? block.ratio.replace(':', ' / ') : '16 / 9'

  return (
    <div
      className="overviewImageBlock"
      style={{
        aspectRatio: ratioStyle,
        borderRadius: 'var(--radius-panel)',
        overflow: 'hidden',
        width: '100%',
        backgroundColor: 'var(--surface-muted, var(--surface))',
      }}
    >
      {src ? (
        <img
          src={src}
          alt={block.alt}
          className="overviewImageBlock__img"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div
          className="overviewImageBlock__placeholder"
          aria-label={block.alt}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            color: 'var(--muted)',
            fontSize: '13px',
          }}
        >
          <span>{block.alt}</span>
        </div>
      )}
    </div>
  )
}

const PeopleBlock = ({
  block,
  tier = 'viewer',
  people = [],
}: {
  block: Extract<OverviewBlock, { type: 'PEOPLE' }>
  tier?: ViewerTier
  people?: TeamPersonRow[]
}) => {
  const isLead = tier === 'lead'

  return (
    <div className="overviewPeopleList" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      {block.items.map((item) => {
        const personRow = people.find((p) => p.uid === item.uid)
        const displayName = personRow?.displayName || item.uid

        return (
          <div
            key={item.uid}
            className="overviewPeopleCard"
            data-uid={item.uid}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '8px 12px',
              borderRadius: 'var(--radius-panel)',
              background: 'var(--surface)',
              border: '1px solid var(--border-subtle, var(--border))',
            }}
          >
            <Avatar name={displayName} size="md" />
            <div className="overviewPeopleCard__info" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <strong
                  className="overviewPeopleCard__name"
                  style={{
                    color: 'var(--text-strong)',
                    fontSize: '13.5px',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </strong>
                {isLead && personRow && (
                  <div
                    className="overviewPeopleCard__stats"
                    data-testid={`people-stats-${item.uid}`}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      fontSize: '12px',
                      fontFamily: 'var(--font-figures)',
                      color: 'var(--muted)',
                      flexShrink: 0,
                    }}
                  >
                    <span>{personRow.open} open</span>
                    <span>{personRow.done7d} done</span>
                    {personRow.overdue > 0 && (
                      <span style={{ color: 'var(--danger)' }}>{personRow.overdue} overdue</span>
                    )}
                  </div>
                )}
              </div>
              {item.note && (
                <div
                  className="overviewPeopleCard__note"
                  style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}
                >
                  {item.note}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const CustomSection = ({
  section,
  toolsNode,
  tier = 'viewer',
  people = [],
  workspaceId = '',
  isPhone = false,
  onNavigateToAnalysis,
  onOpenItem,
  onEditChart,
}: CustomSectionProps) => {
  const { t } = useNls()

  const renderBlock = (block: OverviewBlock) => {
    const widthClass = isPhone
      ? 'overviewBlock--full'
      : block.width === 'full'
        ? 'overviewBlock--full'
        : block.width === 'half'
          ? 'overviewBlock--half'
          : 'overviewBlock--third'

    if (block.type === 'TEXT') {
      return (
        <div
          key={block.id}
          className={`${widthClass} overviewText`}
          data-testid={`overview-block-text-${block.id}`}
          dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(block.markdown) }}
        />
      )
    }

    if (block.type === 'CALLOUT') {
      const toneClass =
        block.tone === 'warning'
          ? 'overviewCallout--warning'
          : block.tone === 'win'
            ? 'overviewCallout--win'
            : 'overviewCallout--note'

      return (
        <div
          key={block.id}
          className={`${widthClass} overviewCallout ${toneClass}`}
          data-testid={`overview-block-callout-${block.id}`}
        >
          {block.title && <div className="overviewCallout__title">{block.title}</div>}
          <div className="overviewCallout__body">{block.body}</div>
        </div>
      )
    }

    if (block.type === 'IMAGE') {
      return (
        <div
          key={block.id}
          className={`${widthClass} overviewBlock`}
          data-testid={`overview-block-image-${block.id}`}
        >
          <ImageBlock block={block} />
        </div>
      )
    }

    if (block.type === 'EMBED') {
      const isAllowed = isEmbedAllowedUrl(block.url)
      const ratioStyle = block.ratio ? block.ratio.replace(':', ' / ') : '16 / 9'

      return (
        <div
          key={block.id}
          className={`${widthClass} overviewBlock`}
          data-testid={`overview-block-embed-${block.id}`}
        >
          {isAllowed ? (
            <iframe
              src={block.url}
              title={t('NLSOverviewEmbedTitle')}
              sandbox="allow-scripts allow-same-origin allow-popups"
              style={{
                width: '100%',
                aspectRatio: ratioStyle,
                border: 0,
                borderRadius: 'var(--radius-panel)',
                display: 'block',
              }}
            />
          ) : (
            <div
              className="overviewBlock__embedFallback"
              data-testid={`embed-fallback-${block.id}`}
              style={{
                padding: '16px',
                borderRadius: 'var(--radius-panel)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 500 }}>
                {t('NLSOverviewEmbedDisallowed')}
              </span>
              <a
                href={block.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '13px',
                  color: 'var(--accent)',
                  wordBreak: 'break-all',
                  textDecoration: 'underline',
                }}
              >
                {block.url}
              </a>
            </div>
          )}
        </div>
      )
    }

    if (block.type === 'LINKS') {
      return (
        <div
          key={block.id}
          className={`${widthClass} overviewBlock`}
          data-testid={`overview-block-links-${block.id}`}
        >
          <div
            className="overviewLinksList"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '10px',
              width: '100%',
            }}
          >
            {block.items.map((item, idx) => {
              if ('url' in item) {
                return (
                  <a
                    key={`url-${idx}`}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="overviewLinkCard"
                    data-testid={`link-item-${idx}`}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-panel)',
                      padding: '12px 14px',
                      textDecoration: 'none',
                      color: 'var(--text-strong)',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      background: 'var(--surface)',
                    }}
                  >
                    <span>{item.label}</span>
                    <small
                      style={{
                        color: 'var(--muted)',
                        fontWeight: 400,
                        fontSize: '11.5px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.url}
                    </small>
                  </a>
                )
              }

              // ref item
              const ref = item.ref
              return (
                <Pressable
                  key={`ref-${ref.id}-${idx}`}
                  type="button"
                  className="overviewLinkCard"
                  data-testid={`link-item-${idx}`}
                  onClick={() => onOpenItem?.(ref)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-panel)',
                    padding: '12px 14px',
                    textAlign: 'left',
                    color: 'var(--text-strong)',
                    fontSize: '13px',
                    fontWeight: 600,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    background: 'var(--surface)',
                    cursor: 'pointer',
                  }}
                >
                  <span>{ref.label || ref.id}</span>
                  <small
                    style={{
                      color: 'var(--muted)',
                      fontWeight: 400,
                      fontSize: '11.5px',
                      textTransform: 'capitalize',
                    }}
                  >
                    {ref.kind}
                  </small>
                </Pressable>
              )
            })}
          </div>
        </div>
      )
    }

    if (block.type === 'PEOPLE') {
      return (
        <div
          key={block.id}
          className={`${widthClass} overviewBlock`}
          data-testid={`overview-block-people-${block.id}`}
        >
          <PeopleBlock block={block} tier={tier} people={people} />
        </div>
      )
    }

    if (block.type === 'CHART') {
      return (
        <div
          key={block.id}
          className={`${widthClass} overviewBlock`}
          data-testid={`overview-block-chart-${block.id}`}
        >
          <ChartPlacementEmbed
            placement={{
              chartId: block.chartId,
              overrides: { height: block.height },
            }}
            workspaceId={workspaceId}
            onOpenInCharts={() => {
              if (onNavigateToAnalysis) {
                onNavigateToAnalysis('CHARTS')
              } else {
                window.location.search = '?tab=CHARTS'
              }
            }}
            onEdit={isPhone ? undefined : onEditChart}
          />
        </div>
      )
    }

    return null
  }

  return (
    <div className="overviewCard" data-testid={`overview-custom-section-${section.id}`}>
      {toolsNode}
      <div className="overviewCard__header">
        <h3 className="overviewCard__title">
          {section.title}
          <span className="overviewPill overviewPill--neutral">
            {t('NLSOverviewCustomSectionTitle')}
          </span>
        </h3>
      </div>

      <div className="overviewCustomBlocks">{section.blocks.map(renderBlock)}</div>
    </div>
  )
}
